import { ApiResponse } from "@/interfaces/ApiResponse";
import router from "@/router";
import { UserModel } from "@/models/User/UserModel"; // Assuming you need this for token parsing

const basePath = process.env.VUE_APP_BASE_URL;

class ApiService {
  private isAuthenticated = false;
  private user = new UserModel(); // Hold the user data

  // Check token validity and restore user session
  private checkTokenValidity() {
    const token = localStorage.getItem("accessToken");
    if (token) {
      const tokenData = this.parseJwt(token);

      if (tokenData) {
        // Check if the token has `exp` and is still valid
        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
        if (tokenData.exp && tokenData.exp > currentTime) {
          // Token is valid
          this.user = new UserModel(tokenData);
          this.isAuthenticated = true;
        } else {
          // Token expired or malformed, remove it
          this.logoutUser();
        }
      } else {
        // If token parsing fails (malformed token), log out the user
        this.logoutUser();
      }
    }
  }

  // Parse JWT token
  private parseJwt(token: string) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = decodeURIComponent(
        atob(base64Url)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(base64);
    } catch (e) {
      console.error("Error parsing token:", e);
      return null;
    }
  }

  // Log out the user and clear the session
  private logoutUser() {
    localStorage.removeItem("accessToken");
    this.isAuthenticated = false;
    this.user = new UserModel(); // Clear user data
    router.push("/login"); // Redirect to login page
  }

  private async request<T>(
    url: string,
    requestOptions: RequestInit
  ): Promise<ApiResponse<T>> {
    try {
      const response = await Promise.race([
        fetch(`${basePath}${url}`, requestOptions),
        new Promise<Response>((_, reject) =>
          setTimeout(() => reject(new Error("Request timeout")), 60000)
        ), // Timeout after 60 seconds
      ]);

      // Check for unauthorized responses (401) and handle them
      if (response.status === 401) {
        this.checkTokenValidity();

        if (!this.isAuthenticated) {
          throw new Error("Unauthorized: Token is invalid or expired");
        }
      }

      // Throw an error if the response status is not OK (4xx or 5xx)
      if (!response.ok) {
        const errorData = await response.json();

        // Return the whole error object including the 'data' field for detailed messages
        throw {
          message: errorData.message || "Request failed",
          data: errorData.data || null, // Ensure the error 'data' field is passed
        };
      }

      const responseData = await response.json();
      const processedData = responseData.data || responseData;

      return {
        message: "Request successful",
        data: processedData,
      };
    } catch (error: any) {
      // Throwing the complete error object to allow detailed error handling
      throw error;
    }
  }

  // Function to add authorization token to the headers if provided
  private addAuthorizationHeader(
    headers: Record<string, string> = {},
    token?: string
  ) {
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  }

  async get<T>(url: string, token?: string): Promise<ApiResponse<T>> {
    const headers = this.addAuthorizationHeader(
      {
        "Content-Type": "application/json",
      },
      token
    );

    const requestOptions: RequestInit = {
      method: "GET",
      headers,
    };

    return this.request<T>(url, requestOptions);
  }

  async post<T>(
    url: string,
    payload: any,
    token?: string
  ): Promise<ApiResponse<T>> {
    const headers = this.addAuthorizationHeader(
      {
        "Content-Type": "application/json",
      },
      token
    );

    const requestOptions: RequestInit = {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    };

    return this.request<T>(url, requestOptions);
  }

  async put<T>(
    url: string,
    payload: any,
    token?: string
  ): Promise<ApiResponse<T>> {
    const headers = this.addAuthorizationHeader(
      {
        "Content-Type": "application/json",
      },
      token
    );

    const requestOptions: RequestInit = {
      method: "PUT",
      headers,
      body: JSON.stringify(payload),
    };

    return this.request<T>(url, requestOptions);
  }

  async delete<T>(url: string, token?: string): Promise<ApiResponse<T>> {
    const headers = this.addAuthorizationHeader(
      {
        "Content-Type": "application/json",
      },
      token
    );

    const requestOptions: RequestInit = {
      method: "DELETE",
      headers,
    };

    return this.request<T>(url, requestOptions);
  }

  async patch<T>(
    url: string,
    payload: any,
    token?: string
  ): Promise<ApiResponse<T>> {
    const headers = this.addAuthorizationHeader(
      {
        "Content-Type": "application/json",
      },
      token
    );

    const requestOptions: RequestInit = {
      method: "PATCH",
      headers,
      body: JSON.stringify(payload),
    };

    return this.request<T>(url, requestOptions);
  }
}

export const apiService = new ApiService();
/**
 * Generates a URL with query parameters from a base URL and a params object
 * @param baseUrl The base URL to which the query parameters will be appended
 * @param params An object containing key-value pairs to be converted into query parameters
 * @returns The full URL with the query parameters appended
 */
export const buildUrlWithParams = (
  baseUrl: string,
  params?: Record<string, string | undefined>
): string => {
  if (!params) return baseUrl;

  const searchParams = new URLSearchParams();

  // Loop through the params object and append each key-value pair
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });

  // Append the query string to the base URL if there are any parameters
  const queryString = searchParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};
